import { Box2, Box3, Euler, Path, ShapePath, Vector2, Vector3 } from "three";
/**
 * Helper class providing utility functions for working with Three.js objects
 * Contains methods for converting between different Three.js types and validating objects
 */
export class ThreeHelpers {
    /**
     * Creates a Vector3 from an object with x, y, and z properties
     * @param vec Object with x, y, and z number properties
     * @returns A new Vector3 with the provided x, y, and z values
     */
    static getVector3FromIVector3(vec) {
        return new Vector3(vec.x, vec.y, vec.z);
    }
    /**
     * Gets the size of a Three.js Object3D as a Vector3
     * @param object The Three.js object to measure
     * @returns Vector3 containing the object's dimensions
     */
    static getVector3SizeFromObject3D(object) {
        return ThreeHelpers.getBox3FromObject3D(object).getSize(new Vector3());
    }
    /**
     * Converts a 3D vector (Vector3) to a 2D vector (Vector2) by discarding the Z component
     * @param vec The 3D vector to convert
     * @returns A 2D vector with the same X and Y components
     */
    static getVector3ToVector2(vec) {
        return new Vector2(vec.x, vec.y);
    }
    /**
     * Converts a 2D vector (Vector2) to a 3D vector (Vector3) by setting Z to 0
     * @param vec The 2D vector to convert
     * @returns A 3D vector with the same X and Y components and Z=0
     */
    static getVector3FromVector2(vec) {
        return new Vector3(vec.x, vec.y, 0);
    }
    /**
     * Creates a Vector2 from an object with x and y properties
     * @param vec Object with x and y number properties
     * @returns A new Vector2 with the provided x and y values
     */
    static getVector2FromIVector2(vec) {
        return new Vector2(vec.x, vec.y);
    }
    /**
     * Converts an array of Vector2 arrays (polygons) to Three.js Shapes
     * @param polygons Array of Vector2 arrays representing polygons
     * @returns Array of Three.js Shapes
     */
    static getShapesFromPolygonPoints(polygons) {
        const shape = new ShapePath();
        shape.subPaths = polygons.map((polygon) => new Path(polygon));
        return shape.toShapes(true);
    }
    /**
     * Creates an Euler rotation from an object with x, y, and z properties
     * @param vec Object with x, y, and z number properties representing rotation in radians
     * @param order The order of rotations, defaults to "XYZ"
     * @returns A new Euler with the provided rotation values
     */
    static getEulerFromIVector3(vec, order = "XYZ") {
        return new Euler(vec.x, vec.y, vec.z, order);
    }
    /**
     * Inverts an Euler rotation
     * @param euler The Euler rotation to invert
     * @returns A new Euler representing the inverted rotation
     */
    static getEulerInverse(euler) {
        return new Euler(-euler.x, -euler.y, -euler.z, euler.order);
    }
    /**
     * Creates a Box2 from an IBox2 interface
     * @param box The IBox2 object to convert
     * @returns A new Box2 with the provided min and max points
     */
    static getBox2FromIBox2(box) {
        return new Box2(ThreeHelpers.getVector2FromIVector2(box.min), ThreeHelpers.getVector2FromIVector2(box.max));
    }
    /**
     * Converts a 3D box (Box3) to a 2D box (Box2) by discarding the Z component
     * @param box The 3D box to convert
     * @returns A 2D box with the same X and Y dimensions
     */
    static getBox2FromBox3(box) {
        return new Box2(ThreeHelpers.getVector3ToVector2(box.min), ThreeHelpers.getVector3ToVector2(box.max));
    }
    /**
     * Creates a Box2 from min and max points
     * @param box Object with min and max points, each with x and y properties
     * @returns A new Box2 with the provided min and max points
     */
    static getBox2FromMinMax(box) {
        return new Box2(ThreeHelpers.getVector2FromIVector2(box.min), ThreeHelpers.getVector2FromIVector2(box.max));
    }
    /**
     * Creates a bounding box (Box3) from a Three.js Object3D
     * @param object The Three.js object to create a bounding box for
     * @returns Box3 containing the object's bounds
     */
    static getBox3FromObject3D(object) {
        return new Box3().setFromObject(object);
    }
    /**
     * Creates a Box3 from an IBox2 interface
     * @param box The IBox2 object to convert
     * @param z Optional object with center and size properties for the Z dimension
     * @returns A new Box3 with the provided min and max points, and Z dimension if specified
     */
    static getBox3FromIBox2(box, z) {
        return new Box3(ThreeHelpers.getVector3FromIVector3({
            x: box.min.x,
            y: box.min.y,
            z: z ? z.center - z.size / 2 : 0,
        }), ThreeHelpers.getVector3FromIVector3({
            x: box.max.x,
            y: box.max.y,
            z: z ? z.center + z.size / 2 : 0,
        }));
    }
    /**
     * Converts a 2D box (Box2) to a 3D box (Box3) by setting Z to 0
     * @param box The 2D box to convert
     * @returns A 3D box with the same X and Y dimensions and Z=0
     */
    static getBox3FromBox2(box) {
        return new Box3(new Vector3(box.min.x, box.min.y, 0), new Vector3(box.max.x, box.max.y, 0));
    }
    /**
     * Converts a Box2 to an RBush-compatible object with minX, minY, maxX, maxY properties
     * @param box2 The Box2 to convert
     * @returns An object with minX, minY, maxX, maxY properties
     */
    static getRBushFromBox2(box2) {
        return { minX: box2.min.x, minY: box2.min.y, maxX: box2.max.x, maxY: box2.max.y };
    }
    /**
     * Checks if a Vector3 has valid (non-NaN) components
     * @param vector3 The Vector3 to validate
     * @returns true if all components are valid numbers, false otherwise
     */
    static isVector3Valid(vector3) {
        if (isNaN(vector3.x)) {
            return false;
        }
        if (isNaN(vector3.y)) {
            return false;
        }
        if (isNaN(vector3.z)) {
            return false;
        }
        return true;
    }
    /**
     * Checks if a Box3 has valid (non-NaN) components in its min and max vectors
     * @param box3 The Box3 to validate
     * @returns true if all components are valid numbers, false otherwise
     */
    static isBox3Valid(box3) {
        return ThreeHelpers.isVector3Valid(box3.min) && ThreeHelpers.isVector3Valid(box3.max);
    }
}
export default ThreeHelpers;
//# sourceMappingURL=three.js.map